/**
 * Single connectivity gate for every background queue.
 *
 * `navigator.onLine` is unreliable in WKWebView: after Airplane Mode is turned
 * off the WebView can keep reporting `false` until the page is re-navigated.
 * Every drain loop gated on it therefore stayed dormant until the driver
 * reopened the appraisal — the reported "no automatic sync on reconnect".
 *
 * Rule: Capacitor's Network plugin is authoritative on native. `navigator.onLine`
 * is only trusted when there is no native status to consult. When in doubt we
 * attempt the pass — a failed request is retried, a skipped pass is not.
 */

import { isNative, getNetworkStatus, refreshNetworkStatus } from "./capacitor";

/** True unless we positively know the device is offline. */
export function isLikelyOnline(): boolean {
  if (isNative) {
    try {
      const status = getNetworkStatus();
      if (typeof status?.connected === "boolean") return status.connected;
    } catch {
      /* plugin unavailable — fall through */
    }
    // Native without a usable status: never block on a stale navigator flag.
    return true;
  }
  if (typeof navigator === "undefined") return true;
  return navigator.onLine !== false;
}

/**
 * True only when we positively know the device is offline.
 *
 * Whenever a drain is about to be skipped we also re-read the authoritative
 * native status in the background, so a missed `networkStatusChange` callback
 * self-heals within one polling cycle instead of stranding every queue until
 * the app is force-closed.
 */
export function isKnownOffline(): boolean {
  const offline = !isLikelyOnline();
  if (offline && isNative) {
    void refreshNetworkStatus("offline-gate");
  }
  return offline;
}

